import React from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { BodySmall } from '@shared/ui/atoms/fonts/Body';
import { SimpleSpinner } from '@shared/ui/atoms/SimpleSpinner';
import { iconPackPath } from '@shared/utils/WhereaboutsHelper';
import { WhereaboutsOption } from '@features/whereabouts/services/WhereaboutOptions';
import { UsersWhereaboutsDay, WhereaboutsGroupType } from '@features/whereabouts/models/movements.models';
import { IconPack } from '@shared/models/company.models';
import {
  selectEditingWhereabouts,
  selectFocusedGroupId,
  selectFocusedUserId,
  startEditingWhereabouts,
} from '@features/whereabouts/store/editMovements.duck';
import EditMovementsPanel from '@shared/ui/organisms/movements-wallchart/components/edit-movements-panel/EditMovementsPanel';
import { CellInfoHover } from './CellInfoHover';
import { EmptyDayCell } from './EmptyDayCell';
import { shouldShowWhereaboutsInfo } from './dailyWhereaboutsUtils';
import {
  CellIcon,
  CellText,
  DeskLabel,
  NoteIndicator,
  SavingClickableCell,
  SavingOverlay,
  UserRow,
  getCellColours,
} from './WhereaboutsV2Styles';

interface Props {
  userId: string;
  firstName: string;
  lastName: string;
  dayData: UsersWhereaboutsDay | undefined;
  amOption: WhereaboutsOption;
  pmOption: WhereaboutsOption;
  officeNameMap: Map<number, string>;
  iconPack: IconPack;
  dateStr: string;
  // Label cells with the whereabouts option (current-user row) instead of the person's name.
  showOptionLabel?: boolean;
}

interface HalfDayCellProps {
  option: WhereaboutsOption;
  half: 'am' | 'pm';
  props: Props;
  isSame: boolean;
  hasNote: boolean;
  isSaving: boolean;
  hoverDates: string[] | undefined;
  wantsHoverInfo: boolean;
  onCellClick: () => void;
}

function HalfDayCell({ option, half, props, isSame, hasNote, isSaving, hoverDates, wantsHoverInfo, onCellClick }: HalfDayCellProps) {
  const { userId, firstName, lastName, dayData, officeNameMap, iconPack, showOptionLabel } = props;
  const colours = getCellColours(option);
  const officeId = half === 'am' ? dayData?.amOfficeId : dayData?.pmOfficeId;
  const deskLabel = half === 'am' ? dayData?.amDeskLabel : dayData?.pmDeskLabel;
  const parkingLabel = half === 'am' ? dayData?.amParkingSpaceLabel : dayData?.pmParkingSpaceLabel;
  const optionItemLabel = half === 'am' ? dayData?.amOptionItemLabel : dayData?.pmOptionItemLabel;
  const officeName = officeId ? officeNameMap.get(officeId) || '' : '';
  const isUnknown = option?.key === 'UNKNOWN';
  const isCustom = !option.required && option.pickable;
  const showNote = hasNote && (half === 'pm' || isSame);
  const spanBoth = half === 'am' && isSame;

  return (
    <CellInfoHover
      style={spanBoth ? { gridColumn: '1 / span 2', display: 'flex' } : { display: 'flex' }}
      noteUserId={wantsHoverInfo ? userId : undefined}
      noteDates={hoverDates}
      trigger={
        <SavingClickableCell $bg={colours.bg} $borderColour={colours.border} onClick={onCellClick}
                       $saving={isSaving}
                       style={{ width: '100%' }}>
          {!isUnknown && <CellIcon src={iconPackPath(iconPack, option.image, option.freeBusyStatus)} alt="" />}
          <CellText>
            <BodySmall weight={600}>{showOptionLabel ? option.label : `${firstName} ${lastName}`}</BodySmall>
            {!showOptionLabel && isCustom && <DeskLabel>{option.label}</DeskLabel>}
            {optionItemLabel && <DeskLabel>{optionItemLabel}</DeskLabel>}
            {(officeName || deskLabel) && <DeskLabel>{officeName}{officeName && deskLabel && ' - '}{deskLabel}</DeskLabel>}
            {parkingLabel && <DeskLabel>{parkingLabel}</DeskLabel>}
          </CellText>
          {showNote && <NoteIndicator><img src="/assets/icons/font/Note.svg" alt="" width={16} height={16} /></NoteIndicator>}
          {isSaving && <SavingOverlay><SimpleSpinner size={20} /></SavingOverlay>}
        </SavingClickableCell>
      }
    />
  );
}

// One person's AM/PM cells for a single day, plus their edit panel.
// Shared by the member rows and the current-user section of the daily view.
export function DailyDayCells(props: Props) {
  const { userId, firstName, lastName, dayData, amOption, pmOption, dateStr, showOptionLabel } = props;
  const dispatch = useDispatch();
  const focusedUserId = useSelector(selectFocusedUserId);
  const focusedGroupId = useSelector(selectFocusedGroupId);
  const editingWhereabouts = useSelector(selectEditingWhereabouts);
  const isEditing = focusedUserId === userId && focusedGroupId === WhereaboutsGroupType.Users;
  const isSaving = !!userId && editingWhereabouts.some(
    w => w.userId === userId && w.date === dateStr && !!w.isWhereaboutsSaving,
  );

  const isSame = amOption.id === pmOption.id
    && (dayData?.amDeskLabel ?? '') === (dayData?.pmDeskLabel ?? '')
    && (dayData?.amOfficeId ?? 0) === (dayData?.pmOfficeId ?? 0)
    && (dayData?.amOptionItemLabel ?? '') === (dayData?.pmOptionItemLabel ?? '');

  const isEmpty = amOption?.key === 'UNKNOWN' && pmOption?.key === 'UNKNOWN' && isSame;
  const hasNote = !!dayData?.dayNote;
  const wantsHoverInfo = shouldShowWhereaboutsInfo(dayData?.dayNote, dayData?.amOptionItemLabel ?? dayData?.pmOptionItemLabel);
  const hoverDates = wantsHoverInfo ? [dateStr] : undefined;
  const amColours = getCellColours(amOption);

  const onCellClick = () => {
    if (!userId || isSaving) return;
    dispatch(startEditingWhereabouts({ userId, date: moment(dateStr), groupId: WhereaboutsGroupType.Users }));
  };

  const halfProps = { props, isSame, hasNote, isSaving, hoverDates, wantsHoverInfo, onCellClick };

  return (
    <>
      <UserRow>
        {isEmpty ? (
          <EmptyDayCell label={showOptionLabel ? amOption.label : `${firstName} ${lastName}`}
                        firstName={firstName}
                        lastName={lastName}
                        bg={amColours.bg}
                        borderColour={amColours.border}
                        saving={isSaving}
                        style={{ gridColumn: '1 / span 2' }}
                        onClick={onCellClick} />
        ) : (
          <HalfDayCell option={amOption} half="am" {...halfProps} />
        )}
        {!isEmpty && !isSame && <HalfDayCell option={pmOption} half="pm" {...halfProps} />}
      </UserRow>
      {isEditing && userId && <EditMovementsPanel isEditing={isEditing} userId={userId} />}
    </>
  );
}
